import {
  COLOR_PALETTES,
  HEATMAP_PALETTES,
} from '@sensehawk/chart-generator/colors';

// The default color palette used when no color palette is explicity set.
export const BI_DEFAULT_PALETTE_COLORS = COLOR_PALETTES.default;

// Color palettes for general charts
export const BI_CHART_COLOR_PALETTES = Object.keys(COLOR_PALETTES).reduce((acc, key) => {
  acc[key] = {
    colors: COLOR_PALETTES[key],
  };
  return acc;
}, {});

// Color palettes for heatmap charts
export const BI_HEATMAP_PALETTES = Object.keys(HEATMAP_PALETTES).reduce((acc, key) => {
  acc[key] = {
    colors: HEATMAP_PALETTES[key],
  };
  return acc;
}, {});

// Mapping which denotes what tabs can be displayed based on the selected chart type.
export const BI_CHART_BUILDER_TABS = {
  pivot_table: ['layout', 'conditional_formatting'],
  table: ['layout', 'conditional_formatting'],
  column_chart: ['layout', 'display', 'axes', 'advanced'],
  horizontalBar_chart: ['layout', 'display', 'axes', 'advanced'],
  line_chart: ['layout', 'display', 'axes', 'advanced'],
  area_chart: ['layout', 'display', 'axes', 'advanced'],
  mixed_chart: ['layout', 'display', 'axes', 'advanced'],
  pie_chart: ['layout', 'display'],
  donut_chart: ['layout', 'display'],
  scatter_chart: ['layout', 'display', 'axes'],
  gauge_chart: ['layout', 'display', 'advanced'],
  progress_chart: ['layout', 'display', 'advanced'],
  heatmap_chart: ['layout', 'display'],
  pyramid_chart: ['layout', 'display'],
  funnel_chart: ['layout', 'display'],
  pareto_chart: ['layout', 'display', 'axes'],
  waterfall_chart: ['layout', 'display', 'axes'],
  number_chart: ['layout', 'display'],
  details_card: ['layout'],
  map: ['layout', 'display', 'conditional_formatting'],
};

// Used to know what is an actual chart (rendered using @sensehawk/chart-generator) and what is not. Example: table, pivot_table, map and metric cards are not actual charts
export const BI_ACTUAL_CHARTS = [
  'column_chart',
  'horizontalBar_chart',
  'line_chart',
  'area_chart',
  'mixed_chart',
  'pie_chart',
  'donut_chart',
  'scatter_chart',
  'gauge_chart',
  'progress_chart',
  'heatmap_chart',
  'pyramid_chart',
  'funnel_chart',
  'pareto_chart',
  'waterfall_chart',
];

// Used to denote what field types can be used in a category in a chart type
export const CHART_TO_CATEGORY_TYPE_MAP = {
  column_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  horizontalBar_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  line_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  area_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  mixed_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  pie_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  donut_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  scatter_chart: ['numeric'],
  heatmap_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  pyramid_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  funnel_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  pareto_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  waterfall_chart: ['text', 'array', 'date', 'boolean', 'numeric'],
  number_chart: ['numeric'],
};

// Used to denote what field types can be used in a value field in a chart type
export const CHART_TO_VALUE_TYPE_MAP = {
  column_chart: ['numeric'],
  horizontalBar_chart: ['numeric'],
  line_chart: ['numeric'],
  area_chart: ['numeric'],
  mixed_chart: ['numeric'],
  pie_chart: ['numeric'],
  donut_chart: ['numeric'],
  scatter_chart: ['numeric'],
  heatmap_chart: ['numeric'],
  gauge_chart: ['numeric'],
  progress_chart: ['numeric'],
  pyramid_chart: ['numeric'],
  funnel_chart: ['numeric'],
  pareto_chart: ['numeric'],
  waterfall_chart: ['numeric'],
  number_chart: ['numeric'],
};

// These are also the only charts that support scale types, tick labels, custom ranges
export const SERIES_SUPPORTED_CHARTS = ['column_chart', 'horizontalBar_chart', 'line_chart', 'area_chart', 'mixed_chart', 'scatter_chart'];

// SERIES_SUPPORTED_CHARTS minus the horizontalBar_chart (because horizontal bar charts can't have dual y-axis)
export const DUAL_Y_AXIS_SUPPORTED_CHARTS = ['column_chart', 'line_chart', 'area_chart', 'mixed_chart', 'scatter_chart'];

// AXES_NAMES_SUPPORTED_CHARTS are the charts which support axes names
export const AXES_NAMES_SUPPORTED_CHARTS = ['column_chart', 'horizontalBar_chart', 'line_chart', 'area_chart', 'mixed_chart', 'waterfall_chart', 'scatter_chart', 'pareto_chart', 'heatmap_chart'];

export const OPERATORS_CONFIG_MAP = {
  in: {
    name: 'in',
    label: 'is',
    description: 'Value is in the specified list',
    supportedTypes: [
      'text',
      'integer',
      'float',
    ],
    supportedResourceTypes: ['members'],
    valueType: 'array',
    component: 'multi_select',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: [
          'active',
          'pending',
        ],
        description: 'Status is active or pending',
      },
    ],
  },
  not_in: {
    name: 'not_in',
    label: 'is not',
    description: 'Value is not in the specified list',
    supportedTypes: [
      'text',
      'integer',
      'float',
    ],
    supportedResourceTypes: ['members'],
    valueType: 'array',
    component: 'multi_select',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: [
          'draft',
          'archived',
        ],
        description: 'Status is not draft or archived',
      },
    ],
  },
  date_is: {
    name: 'date_is',
    label: 'is',
    description: 'Date is a specific value (today, yesterday, this week, etc.)',
    supportedTypes: [
      'date',
      'timestamp',
    ],
    valueType: 'text',
    component: 'select',
    valueRequired: true,
    enums: ['today', 'yesterday', 'this_week', 'this_month', 'this_quarter', 'this_year', 'last_week', 'last_month', 'last_quarter', 'last_year', 'next_week', 'next_month', 'next_quarter', 'next_year'],
    supportsMultipleValues: false,
    examples: [
      {
        value: 'today',
        description: 'Date is today',
      },
    ],
  },
  equals: {
    name: 'equals',
    label: 'equals',
    description: 'Value equals the specified value',
    supportedTypes: [
      'text',
      'integer',
      'float',
      'date',
      'timestamp',
    ],
    component: 'input',
    valueType: 'same_as_field',
    valueRequired: true,
    supportsMultipleValues: true,
    examples: [
      {
        value: 'active',
        description: 'Status equals \'active\'',
      },
      {
        value: [
          'active',
          'pending',
        ],
        description: 'Status equals \'active\' or \'pending\'',
      },
      {
        value: 100,
        description: 'Amount equals 100',
      },
      {
        value: [
          100,
          200,
        ],
        description: 'Amount equals 100 or 200',
      },
      {
        value: '2024-01-01',
        description: 'Date equals January 1, 2024',
      },
    ],
  },
  does_not_equal: {
    name: 'does_not_equal',
    label: 'not equals',
    description: 'Value does not equal the specified value',
    supportedTypes: [
      'text',
      'integer',
      'float',
      'date',
      'timestamp',
    ],
    component: 'input',
    valueType: 'same_as_field',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: 'inactive',
        description: 'Status does not equal \'inactive\'',
      },
      {
        value: 0,
        description: 'Amount does not equal 0',
      },
    ],
  },
  greater_than: {
    name: 'greater_than',
    label: 'greater than',
    description: 'Value is greater than the specified number',
    supportedTypes: [
      'integer',
      'float',
      'date',
      'timestamp',
    ],
    component: 'input',
    valueType: 'same_as_field',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: 100,
        description: 'Amount greater than 100',
      },
      {
        value: '2024-01-01',
        description: 'Date after January 1, 2024',
      },
    ],
  },
  greater_than_or_equal_to: {
    name: 'greater_than_or_equal_to',
    label: 'greater than or equal to',
    description: 'Value is greater than or equal to the specified number',
    supportedTypes: [
      'integer',
      'float',
      'date',
      'timestamp',
    ],
    component: 'input',
    valueType: 'same_as_field',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: 100,
        description: 'Amount greater than or equal to 100',
      },
      {
        value: '2024-01-01',
        description: 'Date on or after January 1, 2024',
      },
    ],
  },
  less_than: {
    name: 'less_than',
    label: 'less than',
    description: 'Value is less than the specified number',
    supportedTypes: [
      'integer',
      'float',
      'date',
      'timestamp',
    ],
    component: 'input',
    valueType: 'same_as_field',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: 1000,
        description: 'Amount less than 1000',
      },
    ],
  },
  less_than_or_equal_to: {
    name: 'less_than_or_equal_to',
    label: 'less than or equal to',
    description: 'Value is less than or equal to the specified number',
    supportedTypes: [
      'integer',
      'float',
      'date',
      'timestamp',
    ],
    component: 'input',
    valueType: 'same_as_field',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: 1000,
        description: 'Amount less than or equal to 1000',
      },
      {
        value: '2024-12-31',
        description: 'Date on or before December 31, 2024',
      },
    ],
  },
  between: {
    name: 'between',
    label: 'between',
    description: 'Value is between the specified start and end values (inclusive)',
    supportedTypes: [
      'integer',
      'float',
      'date',
      'timestamp',
    ],
    component: 'range_input',
    valueType: 'same_as_field',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: [
          100,
          500,
        ],
        description: 'Amount between 100 and 500',
      },
      {
        value: [
          '2024-01-01',
          '2024-12-31',
        ],
        description: 'Date between January 1, 2024 and December 31, 2024',
      },
    ],
  },
  not_between: {
    name: 'not_between',
    label: 'not between',
    description: 'Value is not between the specified start and end values',
    supportedTypes: [
      'integer',
      'float',
      'date',
      'timestamp',
    ],
    component: 'range_input',
    valueType: 'same_as_field',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: [
          100,
          500,
        ],
        description: 'Amount not between 100 and 500',
      },
      {
        value: [
          '2024-01-01',
          '2024-12-31',
        ],
        description: 'Date not in 2024',
      },
    ],
  },
  is_true: {
    name: 'is_true',
    label: 'is true',
    description: 'Boolean field is true',
    supportedTypes: [
      'boolean',
    ],
    valueType: 'boolean',
    component: 'none',
    valueRequired: false,
    supportsMultipleValues: false,
    examples: [
      {
        value: true,
        description: 'Is active is true',
      },
    ],
  },
  is_false: {
    name: 'is_false',
    label: 'is false',
    description: 'Boolean field is false',
    supportedTypes: [
      'boolean',
    ],
    valueType: 'boolean',
    component: 'none',
    valueRequired: false,
    supportsMultipleValues: false,
    examples: [
      {
        value: false,
        description: 'Is active is false',
      },
    ],
  },
  is_empty: {
    name: 'is_empty',
    label: 'is empty',
    description: 'Field is null or empty (for text fields, checks both null and empty string)',
    supportedTypes: [
      'text',
      'integer',
      'float',
      'boolean',
      'date',
      'timestamp',
    ],
    supportedResourceTypes: ['members'],
    valueType: 'none',
    component: 'none',
    valueRequired: false,
    supportsMultipleValues: false,
    examples: [
      {
        value: null,
        description: 'Description is empty',
      },
    ],
  },
  is_not_empty: {
    name: 'is_not_empty',
    label: 'is not empty',
    description: 'Field has a value (for text fields, not null and not empty string)',
    supportedTypes: [
      'text',
      'integer',
      'float',
      'boolean',
      'date',
      'timestamp',
    ],
    supportedResourceTypes: ['members'],
    valueType: 'none',
    component: 'none',
    valueRequired: false,
    supportsMultipleValues: false,
    examples: [
      {
        value: null,
        description: 'Description is not empty',
      },
    ],
  },
  starts_with: {
    name: 'starts_with',
    label: 'starts with',
    description: 'Text starts with the specified string',
    supportedTypes: [
      'text',
    ],
    valueType: 'same_as_field',
    component: 'input',
    valueRequired: true,
    supportsMultipleValues: false,
    options: {
      caseSensitive: {
        label: 'Case Sensitive',
        type: 'checkbox',
        default: false,
        valueMapping: {
          true: 'starts_with',
          false: 'istarts_with',
        },
      },
    },
    examples: [
      {
        value: 'Mr',
        description: 'Name starts with \'Mr\'',
      },
    ],
  },
  ends_with: {
    name: 'ends_with',
    label: 'ends with',
    description: 'Text ends with the specified string',
    supportedTypes: [
      'text',
    ],
    valueType: 'same_as_field',
    component: 'input',
    valueRequired: true,
    supportsMultipleValues: false,
    options: {
      caseSensitive: {
        label: 'Case Sensitive',
        type: 'checkbox',
        default: false,
        valueMapping: {
          true: 'ends_with',
          false: 'iends_with',
        },
      },
    },
    examples: [
      {
        value: 'Jr',
        description: 'Name ends with \'Jr\'',
      },
    ],
  },
  contains: {
    name: 'contains',
    label: 'contains',
    description: 'Text contains the specified substring',
    supportedTypes: [
      'text',
    ],
    valueType: 'same_as_field',
    component: 'input',
    valueRequired: true,
    supportsMultipleValues: false,
    options: {
      caseSensitive: {
        label: 'Case Sensitive',
        type: 'checkbox',
        default: false,
        valueMapping: {
          true: 'contains',
          false: 'icontains',
        },
      },
    },
    examples: [
      {
        value: 'john',
        description: 'Name contains \'john\'',
      },
    ],
  },
  does_not_contain: {
    name: 'does_not_contain',
    label: 'does not contain',
    description: 'Text does not contain the specified substring',
    supportedTypes: [
      'text',
    ],
    valueType: 'same_as_field',
    component: 'input',
    valueRequired: true,
    supportsMultipleValues: false,
    options: {
      caseSensitive: {
        label: 'Case Sensitive',
        type: 'checkbox',
        default: false,
        valueMapping: {
          true: 'does_not_contain',
          false: 'idoes_not_contain',
        },
      },
    },
    examples: [
      {
        value: 'test',
        description: 'Name does not contain \'test\'',
      },
    ],
  },
  contains_any_of: {
    name: 'contains_any_of',
    label: 'contains any of',
    description: 'Array contains at least one of the specified values',
    supportedTypes: [
      'array',
    ],
    supportedResourceTypes: ['members'],
    valueType: 'array',
    component: 'multi_select',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: [
          'tag1',
          'tag2',
        ],
        description: 'Tags contains \'tag1\' or \'tag2\'',
      },
    ],
  },
  contains_all_of: {
    name: 'contains_all_of',
    label: 'contains all of',
    description: 'Array contains all of the specified values',
    supportedTypes: [
      'array',
    ],
    supportedResourceTypes: ['members'],
    valueType: 'array',
    component: 'multi_select',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: [
          'tag1',
          'tag2',
        ],
        description: 'Tags contains both \'tag1\' and \'tag2\'',
      },
    ],
  },
  contains_exactly: {
    name: 'contains_exactly',
    label: 'contains exactly',
    description: 'Array contains exactly the specified values in any order',
    supportedTypes: [
      'array',
    ],
    supportedResourceTypes: ['members'],
    valueType: 'array',
    component: 'multi_select',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: [
          'tag1',
          'tag2',
        ],
        description: 'Tags contains exactly \'tag1\' and \'tag2\'',
      },
    ],
  },
  contains_none_of: {
    name: 'contains_none_of',
    label: 'contains none of',
    description: 'Array contains none of the specified values',
    supportedTypes: [
      'array',
    ],
    supportedResourceTypes: ['members'],
    valueType: 'array',
    component: 'multi_select',
    valueRequired: true,
    supportsMultipleValues: false,
    examples: [
      {
        value: [
          'tag1',
          'tag2',
        ],
        description: 'Tags contains none of \'tag1\' and \'tag2\'',
      },
    ],
  },
  n_to_date: {
    name: 'n_to_date',
    label: 'x to date',
    description: 'Date falls within current N to date (Monday to today). Supports dynamic anchor dates with sign control: "+7_days" (future), "-7_days" (past), and extended units: days, weeks, months, quarters, years',
    supportedTypes: [
      'date',
      'timestamp',
    ],
    valueType: 'text',
    component: 'anchor_date',
    valueRequired: false,
    options: {
      unit: {
        label: 'Unit',
        type: 'select',
        options: [
          'day',
          'week',
          'month',
          'quarter',
          'year',
        ],
        default: 'week',
        valueMapping: {
          week: 'week_to_date',
          month: 'month_to_date',
          quarter: 'quarter_to_date',
          year: 'year_to_date',
        },
      },
    },
    supportsMultipleValues: false,
    examples: [
      {
        op: 'week_to_date',
        value: null,
        description: 'Monday to today',
      },
      {
        op: 'month_to_date',
        value: '2024-07-15',
        description: '1st of July 15 to July 15',
      },
      {
        op: 'quarter_to_date',
        value: '2_weeks',
        description: 'Quarter start 2 weeks ago to 2 weeks ago',
      },
      {
        op: 'year_to_date',
        value: '-30_days',
        description: 'Jan 1st 30 days ago to 30 days ago',
      },
    ],
  },
  relative_period: {
    name: 'relative_period',
    label: 'relative period',
    description: 'Date falls within a relative period (with optional anchor date). Supports dynamic anchor dates with sign control: "+7_days" (future), "-7_days" (past), and extended units: days, weeks, months, quarters, years',
    supportedTypes: [
      'date',
      'timestamp',
    ],
    valueType: 'text',
    component: 'anchor_period',
    valueRequired: true,
    supportsMultipleValues: false,
    options: {
      direction: {
        label: 'Direction',
        type: 'select',
        options: [
          'last',
          'current',
          'next',
        ],
        default: 'last',
        valueMapping: {
          last: 'relative_date_last',
          current: 'relative_date_current',
          next: 'relative_date_next',
        },
      },
    },
    examples: [
      {
        value: '-7_days',
        description: 'Last 7 days from today',
      },
      {
        value: 'week',
        description: 'Last complete week',
      },
      {
        value: [
          '-30_days',
          '2024-07-15',
        ],
        description: 'Last 30 days from July 15, 2024',
      },
    ],
  },
};
